<template>
	<div class="insurance-edit-container">
		<div class="insurance-edit">
			<!-- 自定义标签页头部 - 居中 -->
			<div class="custom-tabs-header">
				<div class="tabs-nav-center">
					<div v-for="tab in tabs" :key="tab.name" :class="['tab-item', { active: activeName === tab.name }]"
						@click="activeName = tab.name">
						{{ tab.label }}
						<span class="tab-badge" v-if="tab.hasError && hasTabError(tab.name)">!</span>
					</div>
				</div>
			</div>

			<!-- 标签内容区域 -->
			<div class="tabs-content">
				<!-- 基本信息 - 居中布局 -->
				<div v-show="activeName === 'basic'" class="tab-pane">
					<div class="tab-content">
						<div class="basic-form-center">
							<el-form class="form-basic-optimized" :model="data" :rules="rules" ref="basicFormRef"
								label-width="100px">
								<div class="form-layout">
									<!-- 左侧区域 -->
									<div class="form-left">
										<el-form-item label="封面图片" prop="main_image">
											<div class="upload-wrapper">
												<page-images :width="160" :height="200" v-model="data.main_image"
													:limit="1" />
												<div class="upload-tip">建议尺寸 320×360px</div>
											</div>
										</el-form-item>
									</div>

									<!-- 右侧区域 -->
									<div class="form-right">
										<div class="form-row">
											<el-form-item label="保险名称" prop="name">
												<el-input v-model="data.name" placeholder="请输入保险名称"
													style="width: 280px;" />
											</el-form-item>
											<el-form-item label="基础价格" prop="price">
												<el-input v-model="data.price" type="number" placeholder="0.00"
													style="width: 200px;">
													<template slot="append">元</template>
												</el-input>
												<div class="form-tip">此价格为展示用基础价格</div>
											</el-form-item>
										</div>

										<div class="form-row">
											<el-form-item label="所属分组" prop="group_ids">
												<el-select v-model="data.group_ids" multiple placeholder="请选择分组"
													style="width: 280px;">
													<el-option v-for="item in groupList" :key="item.id"
														:label="item.name" :value="item.id" />
												</el-select>
											</el-form-item>
											<el-form-item label="保险排序" prop="sort">
												<el-input-number v-model="data.sort" :min="0"
													controls-position="right" />
											</el-form-item>
										</div>

										<el-form-item label="所属分类" prop="category_ids_array">
											<el-cascader v-model="data.category_ids_array" :options="categoryList"
												:props="cascaderProps" placeholder="请选择分类" style="width: 480px;" />
										</el-form-item>

										<el-form-item label="承保公司" prop="insurer_name">
											<el-input v-model="data.insurer_name" placeholder="请输入承保公司"
												style="width: 480px;" />
										</el-form-item>

										<el-form-item label="保险简介" prop="intro">
											<el-input v-model="data.intro" type="textarea" :rows="4"
												placeholder="请输入保险简介" show-word-limit :maxlength="200"
												style="width: 480px;" />
										</el-form-item>

										<el-form-item label="保险状态" prop="status">
											<el-radio-group v-model="data.status">
												<el-radio :label="1">上架</el-radio>
												<el-radio :label="0">下架</el-radio>
											</el-radio-group>
										</el-form-item>
									</div>
								</div>
							</el-form>
						</div>
					</div>
				</div>

				<!-- 产品说明 - 改为折叠面板 -->
				<div v-show="activeName === 'explain'" class="tab-pane">
					<div class="tab-content">
						<div class="section-header-center">
							<h3>产品说明</h3>
							<el-button type="primary" @click="addExplain" :disabled="data.explain.length >= 3"
								size="small">
								新增说明
							</el-button>
						</div>

						<div class="explain-list-center">
							<div class="explain-list">
								<!-- 使用折叠面板展示产品说明 -->
								<el-collapse v-model="activeExplainItems" accordion class="explain-collapse">
									<el-collapse-item v-for="(item, index) in data.explain" :key="index" :name="index"
										class="explain-card">
										<template #title>
											<div class="collapse-title">
												<span class="explain-title">{{ item.name || `说明 ${index + 1}` }}</span>
											</div>
										</template>

										<div class="card-content">
											<div class="card-header">
												<span>说明 {{ index + 1 }}</span>
												<div class="card-actions">
													<el-button size="mini" :disabled="index === 0"
														@click="moveExplainUp(index)">上移</el-button>
													<el-button size="mini" :disabled="index === data.explain.length - 1"
														@click="moveExplainDown(index)">下移</el-button>
													<el-button size="mini" type="danger"
														@click="delExplain(index)">删除</el-button>
												</div>
											</div>

											<el-form :model="item" :rules="explainRules" :ref="`explainFormRef${index}`"
												label-width="80px">
												<el-form-item label="标题" prop="name">
													<el-input v-model="item.name" placeholder="请输入说明标题"
														style="width: 400px;" />
												</el-form-item>
												<el-form-item label="内容" prop="content">
													<el-input v-model="item.content" type="textarea" :rows="3"
														placeholder="请输入说明内容" show-word-limit :maxlength="150"
														style="width: 600px;" />
												</el-form-item>
											</el-form>
										</div>
									</el-collapse-item>
								</el-collapse>

								<div v-if="data.explain.length === 0" class="empty-state">
									<p>暂无产品说明</p>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- 保障计划 - 优化布局 -->
				<div v-show="activeName === 'plans'" class="tab-pane">
					<div class="tab-content">
						<div class="section-header-center">
							<h3>保障计划</h3>
							<el-button type="primary" @click="addSpecs" size="small">新增计划</el-button>
						</div>

						<div class="plans-layout-center">
							<div class="plans-layout-optimized">
								<!-- 水平计划标签页 -->
								<div class="plan-tabs-horizontal">
									<div v-for="(plan, index) in data.specs" :key="index"
										:class="['plan-tab', { active: specsIndex === index }]"
										@click="specsIndex = index">
										<span>{{ plan.name || `计划${index + 1}` }}</span>
										<i class="el-icon-close" @click.stop="delSpecs(index, plan.id)"></i>
									</div>
								</div>

								<!-- 计划详情 -->
								<div class="plan-content-simple" v-if="data.specs[specsIndex]">
									<!-- 计划基本信息 -->
									<div class="plan-basic-card">
										<h4>计划信息</h4>
										<el-form :model="data.specs[specsIndex]" :rules="specsRules"
											:ref="`specsFormRef${specsIndex}`" label-width="100px">
											<div class="form-row">
												<el-form-item label="计划名称" prop="name">
													<el-input v-model="data.specs[specsIndex].name"
														placeholder="请输入计划名称" style="width: 400px;" />
												</el-form-item>
											</div>
										</el-form>
									</div>

									<!-- 时间段价格表 -->
									<div class="time-price-card">
										<h4>时间段价格表</h4>
										<div class="price-table-section">
											<div class="price-table-header">
												<div class="price-table-tip">请按时间顺序设置价格</div>
												<el-button type="primary" @click="addPriceItem(specsIndex)"
													size="small">新增时间段</el-button>
											</div>

											<div class="price-table-container"
												v-if="data.specs[specsIndex].price_items && data.specs[specsIndex].price_items.length > 0">
												<el-table :data="data.specs[specsIndex].price_items" border size="small"
													class="price-table">
													<el-table-column label="最小天数" width="200" prop="min_days">
														<template #default="{ row, $index }">
															<el-input v-model="row.min_days" type="number" :min="1"
																size="mini">
																<template slot="append">天</template>
															</el-input>
														</template>
													</el-table-column>
													<el-table-column label="最大天数" width="200" prop="max_days">
														<template #default="{ row, $index }">
															<el-input v-model="row.max_days" type="number" :min="0"
																size="mini">
																<template slot="append">天</template>
																<template slot="suffix">
																	<span class="max-days-hint"
																		v-if="row.max_days === 0">无上限</span>
																</template>
															</el-input>
														</template>
													</el-table-column>
													<el-table-column label="价格" width="200" prop="price">
														<template #default="{ row }">
															<el-input v-model="row.price" type="number" :min="0"
																:step="0.01" size="mini">
																<template slot="append">元</template>
															</el-input>
														</template>
													</el-table-column>
													<el-table-column label="时间段" width="180">
														<template #default="{ row }">
															<span class="time-range">
																{{ getTimeRangeText(row) }}
															</span>
														</template>
													</el-table-column>
													<el-table-column label="操作" min-width="100" align="center">
														<template #default="{ $index }">
															<el-button type="danger" size="mini" icon="el-icon-delete"
																@click="removePriceItem(specsIndex, $index)" />
														</template>
													</el-table-column>
												</el-table>

												<div class="price-examples">
													<div class="example-title">示例：</div>
													<div class="example-item">1天 2.00元（最小天数：1，最大天数：1）</div>
													<div class="example-item">2天 2.90元（最小天数：2，最大天数：2）</div>
													<div class="example-item">3天 3.80元（最小天数：3，最大天数：3）</div>
													<div class="example-item">4-5天 5.50元（最小天数：4，最大天数：5）</div>
												</div>
											</div>

											<div v-else class="empty-price-table">
												<p>暂无时间段价格设置</p>
												<el-button type="text"
													@click="addPriceItem(specsIndex)">点击添加时间段价格</el-button>
											</div>
										</div>
									</div>

									<!-- 承保范围 -->
									<div class="coverage-range-card">
										<h4>承保范围</h4>
										<el-form :model="data.specs[specsIndex]" :rules="coverageItemRules"
											:ref="`coverageItemFormRef${specsIndex}`" label-width="100px">
											<div class="form-row">
												<el-form-item label="保障年龄" prop="insured_start_age">
													<div class="age-inputs">
														<el-input v-model="data.specs[specsIndex].insured_start_age"
															type="number" placeholder="开始年龄" style="width: 160px;">
															<template slot="append">周岁</template>
														</el-input>
														<span class="age-separator">至</span>
														<el-input v-model="data.specs[specsIndex].insured_end_age"
															type="number" placeholder="结束年龄" style="width: 160px;">
															<template slot="append">周岁</template>
														</el-input>
													</div>
												</el-form-item>
											</div>

											<el-form-item label="承保项目" prop="coverage_items">
												<el-input v-model="data.specs[specsIndex].coverage_items"
													type="textarea" :rows="3" placeholder="请输入承保项目" show-word-limit
													:maxlength="1000" style="width: 700px;" />
											</el-form-item>

											<el-form-item label="不承保项目" prop="exclusion_items">
												<el-input v-model="data.specs[specsIndex].exclusion_items"
													type="textarea" :rows="3" placeholder="请输入不承保项目" show-word-limit
													:maxlength="1000" style="width: 700px;" />
											</el-form-item>
										</el-form>
									</div>

									<!-- 保险详情 -->
									<div class="detail-card">
										<h4>投保提示</h4>
										<page-editor :height="400" v-model="data.specs[specsIndex].detail" />
									</div>

									<!-- 保障项目 - 使用折叠面板 -->
									<div class="coverage-section-simple">
										<div class="section-header">
											<h4>保障项目</h4>
											<el-button type="primary" @click="addCoveragePlan"
												size="small">新增项目</el-button>
										</div>

										<div class="coverage-list-simple">
											<el-collapse v-model="activeCoverageItems" accordion>
												<el-collapse-item
													v-for="(item, index) in data.specs[specsIndex].coverage_plan"
													:key="index" :name="index" class="coverage-card">
													<template #title>
														<div class="collapse-title">
															<span
																class="project-title">{{ item.name || `项目 ${index + 1}` }}</span>
															<span class="project-amount"
																v-if="item.amount">{{ item.amount }}</span>
														</div>
													</template>

													<div class="card-content">
														<div class="card-header">
															<span>项目 {{ index + 1 }}</span>
															<div class="card-actions">
																<el-button size="mini" :disabled="index === 0"
																	@click="movePlanUp(index)">上移</el-button>
																<el-button size="mini"
																	:disabled="index === data.specs[specsIndex].coverage_plan.length - 1"
																	@click="movePlanDown(index)">下移</el-button>
																<el-button size="mini" type="danger"
																	@click="delPlan(index)">删除</el-button>
															</div>
														</div>

														<el-form :model="item" :rules="coverageRules"
															:ref="`coverageFormRef${specsIndex}_${index}`"
															label-width="80px">
															<div class="form-row">
																<el-form-item label="项目标题" prop="name">
																	<el-input v-model="item.name" placeholder="请输入项目标题"
																		style="width: 400px;" />
																</el-form-item>
																<el-form-item label="保障金额" prop="amount">
																	<el-input v-model="item.amount" placeholder="请输入金额"
																		style="width: 200px;" />
																</el-form-item>
															</div>
															<el-form-item label="项目内容" prop="content">
																<el-input v-model="item.content" type="textarea"
																	:rows="3" placeholder="请输入项目内容" show-word-limit
																	style="width: 700px;" />
															</el-form-item>
														</el-form>
													</div>
												</el-collapse-item>
											</el-collapse>

											<div v-if="data.specs[specsIndex].coverage_plan.length === 0"
												class="empty-state">
												<p>暂无保障项目</p>
											</div>
										</div>
									</div>
								</div>

								<div v-if="data.specs.length === 0" class="empty-state">
									<p>暂无保障计划</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- 移动到最外层的底部按钮栏 -->
		<div class="action-bar-fixed">
			<div class="action-content">
				<el-button @click="cancel" size="medium">取消</el-button>
				<el-button @click="validateAndSave" size="medium" type="primary" :loading="loading">
					{{ data.id ? '保存' : '提交' }}
				</el-button>
			</div>
		</div>
	</div>
</template>

<script>
	export default {
		props: {
			id: Number,
			info: Object,
			groupList: Array,
			categoryList: Array,
		},
		data() {
			const validatePrice = (rule, value, callback) => {
				if (!value && value !== 0) return callback(new Error('请输入价格'));
				if (isNaN(value) || value < 0) return callback(new Error('价格必须为大于等于0的数字'));
				callback();
			};

			const validateAge = (rule, value, callback) => {
				if (!value && value !== 0) return callback(new Error('请输入年龄'));
				if (isNaN(value) || value < 0 || value > 120) return callback(new Error('年龄必须在0-120之间'));
				callback();
			};

			const validateAmount = (rule, value, callback) => {
				if (!value && value !== 0) return callback(new Error('请输入金额'));
				callback();
			};

			const validateInsurer = (rule, value, callback) => {
				if (!value) return callback(new Error('请输入承保公司'));
				callback();
			};

			return {
				loading: false,
				activeName: 'basic',
				specsIndex: 0,
				activeExplainItems: [], // 当前展开的产品说明
				activeCoverageItems: [], // 当前展开的保障项目
				tabs: [{
						label: '基本信息',
						name: 'basic',
						hasError: true
					},
					{
						label: '产品说明',
						name: 'explain',
						hasError: true
					},
					{
						label: '保障计划',
						name: 'plans',
						hasError: true
					}
				],
				data: {
					sort: 0,
					status: 1,
					explain: [],
					specs: [],
					insurer_name: '',
				},
				cascaderProps: {
					multiple: true,
					value: 'id',
					label: 'name',
				},
				rules: {
					name: [{
						required: true,
						message: '请输入保险名称',
						trigger: 'blur'
					}],
					intro: [{
						required: true,
						message: '请输入保险简介',
						trigger: 'blur'
					}],
					main_image: [{
						required: true,
						message: '请上传封面图',
						trigger: 'change'
					}],
					price: [{
						required: true,
						validator: validatePrice,
						trigger: 'blur'
					}],
					category_ids_array: [{
						required: true,
						message: '请选择所属分类',
						trigger: 'change'
					}],
					insurer_name: [{
						required: true,
						validator: validateInsurer,
						message: '请输入承保公司',
						trigger: 'blur'
					}],
				},
				explainRules: {
					name: [{
						required: true,
						message: '请输入说明标题',
						trigger: 'blur'
					}],
					content: [{
						required: true,
						message: '请输入说明内容',
						trigger: 'blur'
					}]
				},
				specsRules: {
					name: [{
						required: true,
						message: '请输入计划名称',
						trigger: 'blur'
					}],
					base_price: [{
						required: true,
						validator: validatePrice,
						trigger: 'blur'
					}]
				},
				coverageRules: {
					name: [{
						required: true,
						message: '请输入保障项目标题',
						trigger: 'blur'
					}],
					amount: [{
						required: true,
						message: '请输入保障金额',
						trigger: 'blur'
					}],
					content: [{
						required: true,
						message: '请输入保障项目内容',
						trigger: 'blur'
					}]
				},
				coverageItemRules: {
					insured_start_age: [{
						required: true,
						validator: validateAge,
						trigger: 'blur'
					}],
					insured_end_age: [{
						required: true,
						validator: validateAge,
						trigger: 'blur'
					}],
					coverage_items: [{
						required: true,
						message: '请输入承保项目',
						trigger: 'blur'
					}],
					exclusion_items: [{
						required: true,
						message: '请输入不承保项目',
						trigger: 'blur'
					}]
				}
			}
		},
		mounted() {
			this.getData(this.id);
		},
		methods: {
			hasTabError(tabName) {
				return false;
			},

			addExplain() {
				if (this.data.explain.length < 3) {
					this.data.explain.push({
						name: '',
						content: ''
					});
				}
			},
			addCoveragePlan() {
				this.data.specs[this.specsIndex].coverage_plan.push({
					name: '',
					amount: '',
					content: ''
				});
			},
			addSpecs() {
				this.data.specs.push({
					name: '',
					price_items: [], // 确保新计划有 price_items 数组
					coverage_plan: [{
						name: '',
						amount: '',
						content: ''
					}],
					insured_start_age: '',
					insured_end_age: '',
					coverage_items: '',
					exclusion_items: '',
					detail: '',
				});
				this.specsIndex = this.data.specs.length - 1;
			},

			// 新增时间段价格项
			addPriceItem(specIndex) {
				// 确保 price_items 数组存在
				if (!this.data.specs[specIndex].price_items) {
					this.$set(this.data.specs[specIndex], 'price_items', []);
				}

				const priceItems = this.data.specs[specIndex].price_items;

				// 计算最小天数
				let minDays = 1;
				if (priceItems.length > 0) {
					const lastItem = priceItems[priceItems.length - 1];
					minDays = parseInt(lastItem.max_days) + 1;
				}

				// 新增时间段
				const newItem = {
					min_days: minDays,
					max_days: minDays, // 默认和最小天数相同
					price: '',
				};

				priceItems.push(newItem);

				// 立即验证新增的项
				setTimeout(() => {
					this.validatePriceItem(newItem, priceItems.length - 1, specIndex);
				}, 100);
			},

			// 移除时间段价格项
			removePriceItem(specIndex, priceIndex) {
				this.data.specs[specIndex].price_items.splice(priceIndex, 1);
			},

			// 获取时间段文本
			getTimeRangeText(item) {
				if (item.max_days === 0) {
					return `${item.min_days}天及以上`;
				} else if (item.min_days === item.max_days) {
					return `${item.min_days}天`;
				} else {
					return `${item.min_days}-${item.max_days}天`;
				}
			},

			movePlanUp(index) {
				if (index === 0) return;
				const plans = this.data.specs[this.specsIndex].coverage_plan;
				const temp = plans[index];
				plans[index] = plans[index - 1];
				plans[index - 1] = temp;
			},
			movePlanDown(index) {
				const plans = this.data.specs[this.specsIndex].coverage_plan;
				if (index === plans.length - 1) return;
				const temp = plans[index];
				plans[index] = plans[index + 1];
				plans[index + 1] = temp;
			},
			moveExplainUp(index) {
				if (index === 0) return;
				const temp = this.data.explain[index];
				this.data.explain[index] = this.data.explain[index - 1];
				this.data.explain[index - 1] = temp;
			},
			moveExplainDown(index) {
				if (index === this.data.explain.length - 1) return;
				const temp = this.data.explain[index];
				this.data.explain[index] = this.data.explain[index + 1];
				this.data.explain[index + 1] = temp;
			},
			delPlan(index) {
				this.data.specs[this.specsIndex].coverage_plan.splice(index, 1);
			},
			delExplain(index) {
				this.data.explain.splice(index, 1);
			},
			delSpecs(index, id) {
				this.$confirm('确认删除该保障计划吗?', '提示', {
					confirmButtonText: '确定',
					cancelButtonText: '取消',
					type: 'warning'
				}).then(() => {
					this.data.specs.splice(index, 1);
					if (id) this.$del('app-goods-specs/' + id);
					if (this.specsIndex >= index) this.specsIndex = Math.max(0, this.specsIndex - 1);
				});
			},
			getData(id) {
				if (!id) return;

				this.$get('app-goods/' + id).then(res => {
					this.data = res.data;
					// 处理后端返回的数据格式
					// 1. 确保 specs 存在
					if (!this.data.specs) {
						this.$set(this.data, 'specs', []);
					}

					// 2. 确保每个 spec 都有必要的字段
					this.data.specs.forEach((spec, index) => {
						// 确保有 price_items 数组
						if (!spec.price_items) {
							this.$set(spec, 'price_items', []);
						}

						// 确保有 coverage_plan 数组
						if (!spec.coverage_plan) {
							this.$set(spec, 'coverage_plan', []);
						}

						// 确保其他必要字段存在
						const requiredFields = [
							'name', 'base_price', 'insured_start_age', 'insured_end_age',
							'coverage_items', 'exclusion_items', 'detail'
						];

						requiredFields.forEach(field => {
							if (spec[field] === undefined) {
								this.$set(spec, field, '');
							}
						});

					});

					// 如果有 specs 数据，默认选中第一个
					if (this.data.specs.length > 0) {
						this.specsIndex = 0;
					}

				}).catch(err => {
					this.$toast(err);
				});
			},
			async validateAndSave() {
				try {
					this.loading = true;

					await this.validateBasicInfo();
					await this.validateExplains();
					await this.validateSpecs();
					await this.validatePriceItems();
					this.saveData();
				} catch (error) {
					this.$message.error(error.message);
					this.loading = false;
				}
			},
			validateBasicInfo() {
				return new Promise((resolve, reject) => {
					this.$refs.basicFormRef.validate(valid => {
						if (valid) {
							resolve();
						} else {
							this.activeName = 'basic';
							reject(new Error('请完善基本信息'));
						}
					});
				});
			},
			validateExplains() {
				return new Promise((resolve, reject) => {
					if (this.data.explain.length === 0) {
						this.activeName = 'explain';
						return reject(new Error('请至少添加一个保险说明'));
					}

					let hasError = false;
					this.data.explain.forEach((item, index) => {
						const formRef = this.$refs[`explainFormRef${index}`];
						if (formRef && formRef[0]) {
							formRef[0].validate(valid => {
								if (!valid) hasError = true;
							});
						}
					});

					if (hasError) {
						this.activeName = 'explain';
						reject(new Error('请完善保险说明'));
					} else {
						resolve();
					}
				});
			},

			// 验证时间段价格
			validatePriceItems() {
				return new Promise((resolve, reject) => {
					let hasError = false;
					let errorSpecIndex = -1;

					this.data.specs.forEach((spec, specIndex) => {
						if (!spec.price_items || spec.price_items.length === 0) {
							hasError = true;
							errorSpecIndex = specIndex;
							this.$message.error(`计划 "${spec.name || `计划${specIndex + 1}`}" 未设置时间段价格`);
							return;
						}
					});

					if (hasError) {
						this.activeName = 'plans';
						if (errorSpecIndex >= 0) {
							this.specsIndex = errorSpecIndex;
						}
						reject(new Error('请完善时间段价格设置'));
					} else {
						resolve();
					}
				});
			},

			validateSpecs() {
				return new Promise((resolve, reject) => {
					if (this.data.specs.length === 0) {
						this.activeName = 'plans';
						return reject(new Error('请至少添加一个保障计划'));
					}

					let hasError = false;
					this.data.specs.forEach((spec, index) => {
						const specsFormRef = this.$refs[`specsFormRef${index}`];
						if (specsFormRef && specsFormRef[0]) {
							specsFormRef[0].validate(valid => {
								if (!valid) hasError = true;
							});
						}

						if (spec.coverage_plan.length === 0) {
							hasError = true;
							this.activeName = 'plans';
							this.specsIndex = index;
						}

						spec.coverage_plan.forEach((plan, planIndex) => {
							const coverageFormRef = this.$refs[
								`coverageFormRef${index}_${planIndex}`];
							if (coverageFormRef && coverageFormRef[0]) {
								coverageFormRef[0].validate(valid => {
									if (!valid) hasError = true;
								});
							}
						});

						const coverageItemFormRef = this.$refs[`coverageItemFormRef${index}`];
						if (coverageItemFormRef && coverageItemFormRef[0]) {
							coverageItemFormRef[0].validate(valid => {
								if (!valid) hasError = true;
							});
						}
					});

					if (hasError) {
						this.activeName = 'plans';
						reject(new Error('请完善保障计划'));
					} else {
						resolve();
					}
				});
			},
			// 在 saveData 方法中修改 submitData 的构建
			saveData() {
			    // 处理价格数据：将时间段价格包含在 specs 数据中
			    const submitData = {
			        ...this.data,
			        group_ids: this.data.group_ids ? this.data.group_ids.toString() : '',
			        explain: JSON.stringify(this.data.explain),
			
			        // 将时间段价格包含在 specs 中一起提交
			        specs: JSON.stringify(this.data.specs.map(spec => {
			            // 创建要保存的数据对象，与后端字段对应
			            const specsData = {
			                id: spec.id || null,
			                name: spec.name,
			                coverage_plan: spec.coverage_plan || [],
			                sort: spec.sort || 0,
			                coverage_items: spec.coverage_items || '',
			                exclusion_items: spec.exclusion_items || '',
			                detail: spec.detail || '',
			                insured_start_age: spec.insured_start_age || '',
			                insured_end_age: spec.insured_end_age || '',
			                goods_id: this.data.id || null, // 商品ID
			
			                // 直接包含时间段价格数据（后端会识别 price_items）
			                price_items: spec.price_items ? spec.price_items.map(item => ({
			                    min_days: item.min_days || 1,
			                    max_days: item.max_days || 0,
			                    price: item.price || 0,
			                })) : []
			            };
			
			            return specsData;
			        })),
			
			        category_ids_array: JSON.stringify(this.data.category_ids_array)
			    };
			
			    // 添加调试信息
			    console.log('提交的数据：', submitData);
			    console.log('specs 数据：', JSON.parse(submitData.specs));
			
			    // 构建请求 URL
			    const url = this.data.id ? `app-goods-save/${this.data.id}` : 'app-goods-save/0';
			
			    // 使用统一的保存接口
			    this.$post(url, submitData).then(res => {
			        this.$emit('save');
			        this.loading = false;
			        this.$message.success(this.data.id ? '更新成功' : '创建成功');
			
			        // 如果有新的ID，更新本地数据
			        if (res.data && res.data.id && !this.data.id) {
			            this.data.id = res.data.id;
			        }
			    }).catch(err => {
			        // 显示详细的错误信息
			        if (err.response && err.response.data) {
			            this.$message.error(err.response.data.message || '保存失败');
			        } else {
			            this.$message.error(err.message || '保存失败');
			        }
			        this.loading = false;
			    });
			},

			cancel() {
				this.$emit('cancel');
			},
		},
	}
</script>

<style lang="scss" scoped>
	.insurance-edit-container {
		height: 100vh;
		display: flex;
		flex-direction: column;
		background: #f5f7fa;
		position: relative;
	}

	.insurance-edit {
		flex: 1;
		display: flex;
		flex-direction: column;
		padding: 20px;
		overflow: hidden;
		position: relative;
	}

	/* 自定义标签页样式 - 居中 */
	.custom-tabs-header {
		background: white;
		border-radius: 8px 8px 0 0;
		border-bottom: 1px solid #e4e7ed;
		position: sticky;
		top: 0;
		z-index: 100;
	}

	.tabs-nav-center {
		display: flex;
		justify-content: center;
		padding: 0 20px;
	}

	.tab-item {
		padding: 16px 24px;
		cursor: pointer;
		border-bottom: 2px solid transparent;
		transition: all 0.3s;
		position: relative;
		font-size: 14px;
		color: #606266;

		&:hover {
			color: #409eff;
		}

		&.active {
			color: #409eff;
			border-bottom-color: #409eff;
			font-weight: 500;
		}
	}

	.tab-badge {
		position: absolute;
		top: 8px;
		right: 8px;
		width: 6px;
		height: 6px;
		background: #f56c6c;
		border-radius: 50%;
	}

	/* 标签内容区域 */
	.tabs-content {
		flex: 1;
		background: white;
		border-radius: 0 0 8px 8px;
		overflow: auto;
		/* 为底部按钮栏留出空间 */
		margin-bottom: 60px;
	}

	.tab-pane {
		height: 100%;
	}

	.tab-content {
		padding: 20px;
		height: 100%;
		overflow: auto;
	}

	/* 基本信息表单居中 */
	.basic-form-center {
		display: flex;
		justify-content: center;
	}

	/* 优化后的基本信息表单 */
	.form-basic-optimized {
		max-width: 1000px;
		width: 100%;

		.form-layout {
			display: flex;
			gap: 40px;
			align-items: flex-start;
		}

		.form-left {
			flex: 0 0 auto;
		}

		.form-right {
			flex: 1;
			display: flex;
			flex-direction: column;
			gap: 20px;
		}

		.form-row {
			display: flex;
			gap: 20px;
			align-items: flex-start;
		}

		.el-form-item {
			margin-bottom: 20px;
		}

		.upload-wrapper {
			.upload-tip {
				font-size: 12px;
				color: #909399;
				margin-top: 8px;
				text-align: center;
			}
		}

		.form-tip {
			font-size: 12px;
			color: #909399;
			margin-top: 4px;
		}
	}

	/* 居中布局的容器 */
	.section-header-center {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 16px;
		max-width: 1000px;
		margin-left: auto;
		margin-right: auto;

		h3 {
			margin: 0;
			color: #303133;
			font-size: 16px;
		}
	}

	.explain-list-center {
		display: flex;
		justify-content: center;
	}

	.plans-layout-center {
		display: flex;
		justify-content: center;
	}

	/* 产品说明区域 - 加宽 */
	.explain-list {
		display: flex;
		flex-direction: column;
		gap: 16px;
		max-width: 1000px;
		width: 100%;
	}

	/* 产品说明折叠面板样式 */
	.explain-collapse {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.explain-card {
		border: 1px solid #e4e7ed;
		border-radius: 6px;
		overflow: hidden;

		&:not(:last-child) {
			margin-bottom: 8px;
		}

		/* 自定义折叠面板标题 */
		:deep(.el-collapse-item__header) {
			background: #f8f9fa;
			padding: 12px 16px;
			border-bottom: none;
			font-weight: 500;

			.collapse-title {
				display: flex;
				justify-content: space-between;
				align-items: center;
				width: 100%;
				padding-right: 16px;

				.explain-title {
					font-size: 14px;
					color: #303133;
					font-weight: 500;
				}
			}
		}

		/* 自定义折叠面板内容 */
		:deep(.el-collapse-item__wrap) {
			background: white;
			border-bottom: none;
		}

		:deep(.el-collapse-item__content) {
			padding: 0;
		}

		.card-content {
			padding: 16px;
			background: white;
		}

		.card-header {
			display: flex;
			justify-content: space-between;
			align-items: center;
			margin-bottom: 16px;
			padding-bottom: 8px;
			border-bottom: 1px solid #e4e7ed;

			.card-actions {
				display: flex;
				gap: 8px;
			}
		}
	}

	/* 简化版保障计划布局 - 加宽 */
	.plans-layout-optimized {
		display: flex;
		flex-direction: column;
		gap: 16px;
		min-height: 600px;
		max-width: 1000px;
		width: 100%;
	}

	/* 水平计划标签页 */
	.plan-tabs-horizontal {
		display: flex;
		gap: 8px;
		overflow-x: auto;
		padding: 8px 0;
		background: white;
		border-bottom: 1px solid #e4e7ed;
	}

	.plan-tab {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 8px 16px;
		border: 1px solid #e4e7ed;
		border-radius: 4px;
		cursor: pointer;
		background: white;
		transition: all 0.3s;
		white-space: nowrap;
		flex-shrink: 0;

		&:hover {
			border-color: #409eff;
		}

		&.active {
			background: #409eff;
			color: white;
			border-color: #409eff;
		}

		.el-icon-close {
			opacity: 0.6;
			font-size: 12px;

			&:hover {
				opacity: 1;
			}
		}
	}

	/* 计划详情 */
	.plan-content-simple {
		display: flex;
		flex-direction: column;
		gap: 20px;
	}

	/* 卡片样式 - 调整宽度 */
	.plan-basic-card,
	.coverage-range-card,
	.detail-card {
		background: white;
		border: 1px solid #e4e7ed;
		border-radius: 6px;
		padding: 16px 20px;

		h4 {
			margin: 0 0 16px 0;
			padding-bottom: 8px;
			border-bottom: 1px solid #e4e7ed;
			color: #303133;
			font-size: 15px;
		}

		.form-tip {
			font-size: 12px;
			color: #909399;
			margin-top: 4px;
		}
	}

	/* 时间段价格卡片样式 */
	.time-price-card {
		background: white;
		border: 1px solid #e4e7ed;
		border-radius: 6px;
		padding: 16px 20px;

		h4 {
			margin: 0 0 16px 0;
			padding-bottom: 8px;
			border-bottom: 1px solid #e4e7ed;
			color: #303133;
			font-size: 15px;
		}
	}

	.price-table-section {
		.price-table-header {
			display: flex;
			justify-content: space-between;
			align-items: center;
			margin-bottom: 16px;

			.price-table-tip {
				font-size: 12px;
				color: #909399;
			}
		}

		.price-table-container {
			margin-top: 12px;

			.price-table {
				width: 100%;
				margin-bottom: 16px;

				:deep(.el-input__inner) {
					text-align: center;
				}

				.max-days-hint {
					font-size: 12px;
					color: #909399;
					margin-left: 4px;
				}

				.time-range {
					font-weight: 500;
					color: #409eff;
				}
			}

			.price-examples {
				background: #f8f9fa;
				border: 1px solid #e4e7ed;
				border-radius: 4px;
				padding: 12px 16px;
				margin-top: 12px;

				.example-title {
					font-weight: 500;
					margin-bottom: 8px;
					color: #606266;
				}

				.example-item {
					font-size: 12px;
					color: #909399;
					margin-bottom: 4px;

					&:last-child {
						margin-bottom: 0;
					}
				}
			}
		}

		.empty-price-table {
			text-align: center;
			padding: 40px;
			color: #909399;
			background: #f8f9fa;
			border-radius: 4px;
			border: 1px dashed #dcdfe6;

			p {
				margin-bottom: 8px;
			}
		}
	}

	/* 保障项目区域 - 使用折叠面板 */
	.coverage-section-simple {
		background: white;
		border: 1px solid #e4e7ed;
		border-radius: 6px;
		padding: 16px 20px;

		.section-header {
			display: flex;
			justify-content: space-between;
			align-items: center;
			margin-bottom: 16px;
			padding-bottom: 8px;
			border-bottom: 1px solid #e4e7ed;

			h4 {
				margin: 0;
				color: #303133;
				font-size: 15px;
			}
		}
	}

	.coverage-list-simple {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	/* 折叠面板样式 */
	.coverage-card {
		border: 1px solid #e4e7ed;
		border-radius: 6px;
		overflow: hidden;

		&:not(:last-child) {
			margin-bottom: 8px;
		}

		/* 自定义折叠面板标题 */
		:deep(.el-collapse-item__header) {
			background: #f8f9fa;
			padding: 12px 16px;
			border-bottom: none;
			font-weight: 500;

			.collapse-title {
				display: flex;
				justify-content: space-between;
				align-items: center;
				width: 100%;
				padding-right: 16px;

				.project-title {
					font-size: 14px;
					color: #303133;
					font-weight: 500;
				}

				.project-amount {
					font-size: 14px;
					color: #409eff;
					font-weight: 600;
				}
			}
		}

		/* 自定义折叠面板内容 */
		:deep(.el-collapse-item__wrap) {
			background: white;
			border-bottom: none;
		}

		:deep(.el-collapse-item__content) {
			padding: 0;
		}

		.card-content {
			padding: 16px;
			background: white;
		}

		.card-header {
			display: flex;
			justify-content: space-between;
			align-items: center;
			margin-bottom: 16px;
			padding-bottom: 8px;
			border-bottom: 1px solid #e4e7ed;

			.card-actions {
				display: flex;
				gap: 8px;
			}
		}
	}

	/* 表单布局 */
	.form-row {
		display: flex;
		gap: 20px;
		align-items: flex-start;
		margin-bottom: 16px;
	}

	.age-inputs {
		display: flex;
		align-items: center;
		gap: 12px;

		.age-separator {
			color: #909399;
			font-size: 14px;
		}
	}

	/* 其他样式 */
	.empty-state {
		text-align: center;
		padding: 40px;
		color: #909399;
	}

	/* 固定在底部的按钮栏 - 绝对定位 */
	.action-bar-fixed {
		position: fixed;
		bottom: 0;
		left: 0;
		right: 0;
		background: white;
		border-top: 1px solid #e4e7ed;
		padding: 16px 0;
		z-index: 1000;
		box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.1);
	}

	.action-content {
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 20px;
		text-align: center;

		.el-button {
			width: 120px;
			margin: 0 8px;
		}
	}

	/* 响应式设计 */
	@media (max-width: 768px) {
		.insurance-edit {
			padding: 10px;
			padding-bottom: 70px; /* 移动端增加底部内边距 */
		}
		
		.tabs-content {
			margin-bottom: 70px; /* 移动端调整间距 */
		}

		.tabs-nav-center {
			padding: 0 10px;
			justify-content: flex-start;
			overflow-x: auto;
		}

		.tab-item {
			padding: 12px 16px;
			font-size: 13px;
			flex-shrink: 0;
		}

		.action-content {
			padding: 0 10px;

			.el-button {
				width: 100px;
				margin: 0 4px;
			}
		}
		
		.action-bar-fixed {
			padding: 12px 0; /* 移动端减小内边距 */
		}

		.form-basic-optimized {
			.form-layout {
				flex-direction: column;
				gap: 20px;
			}

			.form-row {
				flex-direction: column;
				gap: 16px;
			}
		}

		.plan-tabs-horizontal {
			padding: 8px 4px;
		}

		.plan-tab {
			padding: 6px 12px;
			font-size: 13px;
		}

		.plan-basic-card,
		.coverage-range-card,
		.detail-card,
		.coverage-section-simple,
		.explain-card,
		.time-price-card {
			padding: 12px 16px;
		}

		.explain-card,
		.coverage-card {
			:deep(.el-collapse-item__header) {
				padding: 10px 12px;

				.collapse-title {
					flex-direction: column;
					align-items: flex-start;
					gap: 4px;
				}
			}

			.card-content {
				padding: 12px;
			}

			.form-row {
				flex-direction: column;
				gap: 16px;
			}
		}

		.price-table-header {
			flex-direction: column;
			align-items: flex-start !important;
			gap: 8px;
		}

		.price-examples {
			padding: 8px 12px !important;
		}

		:deep(.el-table) {
			font-size: 12px;

			.el-button--mini {
				padding: 4px 8px;
			}
		}

		/* 移动端取消最大宽度限制 */
		.basic-form-center,
		.section-header-center,
		.explain-list,
		.plans-layout-optimized {
			max-width: 100%;
		}
	}
</style>